import React from 'react';
import { FormControlPageVariablesProps, ListItemConfig, ListItemValue, TagSource, VariableData } from './index';
import { Box, FormLabel, Grid, Input, Stack, Typography } from '@mui/material';
import ColorPicker from 'components/common/color-picker/ColorPicker';
import UploadFile from 'components/common/upload-control/UploadFile';
import styles from './ListItem.styles';
import { pageAPI } from 'api/editor/page';
import AsyncReactSelect from 'components/common/react-select/AsyncReactSelect';
import { FormComponentValue } from 'components/common/form/layout/control';
import { TListType } from '../index';
import TextHtmlVariable from './TextHtmlVariable';
import { YNValues } from 'tools/types';

function ListItem({
    itemValue,
    listConfig,
    tagUID,
    itemIndex,
    controlProps,
    updateListItemValue,
    setShowLoader,
    loadOptions,
    tag,
}: {
    itemValue: ListItemValue;
    listConfig: Array<ListItemConfig> | string;
    tagUID: string;
    itemIndex: number;
    controlProps: FormControlPageVariablesProps;
    updateListItemValue: (valueName: string, value: string) => void;
    setShowLoader: (value: boolean) => void;
    loadOptions: (
        inputValue: string,
        options: string,
        source: TagSource,
        key: string,
        multiple: YNValues,
        init?: boolean,
        index?: number,
    ) => Promise<FormComponentValue[]>;
    tag: VariableData;
}) {
    const uploadButtonClick = (
        event: React.ChangeEvent<HTMLInputElement>,
        tagUid: string,
        listItem: number,
        optionIndex: number,
        valueName: string,
    ) => {
        if (!event.target || !event.target.files || controlProps.uploadAssetUrl === '') {
            return;
        }

        setShowLoader(true);
        const file = event.target.files[0];
        const formData = new FormData();
        formData.append('file', file, file.name);
        formData.append('listItem', listItem.toString());
        formData.append('optionIndex', optionIndex.toString());

        pageAPI
            .uploadAsset(formData, `${controlProps.uploadAssetUrl}/${tagUid}`)
            .then((response) => {
                if (response.status === 'OK') {
                    if (response?.relative_path) {
                        updateListItemValue(
                            valueName,
                            '/p/builder-component/' + response.relative_path.replaceAll('\\', '/'),
                        );
                    } else {
                        updateListItemValue(valueName, response.file_name);
                    }
                }
            })
            .finally(() => {
                setShowLoader(false);
            });
    };

    if (listConfig === '') {
        return (
            <Box sx={styles.listItemsWrapper}>
                <Stack direction={'column'} key={'tag_' + tagUID + '_list_item_' + itemIndex + '_text_area_' + 0}>
                    {tag.use_raw_html_ind === 'Y' ? (
                        <TextHtmlVariable
                            name={tag.name + '-' + itemIndex}
                            value={itemValue.value as string}
                            onChange={(newValue) => updateListItemValue(listConfig, newValue)}
                        />
                    ) : (
                        <Input
                            value={itemValue.value}
                            onChange={(event) => {
                                updateListItemValue(listConfig, event.target.value);
                            }}
                            multiline
                            minRows={1}
                            inputProps={{ 'data-1p-ignore': true }}
                        />
                    )}
                </Stack>
            </Box>
        );
    }

    return typeof listConfig === 'object' ? (
        <Stack sx={styles.listItemsWrapper} spacing={1}>
            {listConfig.map((config, valueIndex) => {
                if (typeof config === 'string') {
                    return (
                        <Stack
                            direction={'column'}
                            key={'tag_' + tagUID + '_list_item_' + itemIndex + '_text_area_' + valueIndex}
                        >
                            <FormLabel>{config}</FormLabel>
                            {tag.use_raw_html_ind === 'Y' ? (
                                <TextHtmlVariable
                                    name={tag.name + '-' + itemIndex}
                                    value={itemValue[config]}
                                    onChange={(newValue) => updateListItemValue(config, newValue)}
                                />
                            ) : (
                                <Input
                                    value={itemValue[config]}
                                    onChange={(event) => {
                                        updateListItemValue(config, event.target.value);
                                    }}
                                    multiline
                                    minRows={1}
                                    inputProps={{ 'data-1p-ignore': true }}
                                />
                            )}
                        </Stack>
                    );
                }
                if (typeof itemValue === 'object') {
                    if (config.type === 'textarea') {
                        return (
                            <Stack
                                direction={'column'}
                                key={'tag_' + tagUID + '_list_item_' + itemIndex + '_text_area_' + valueIndex}
                            >
                                <FormLabel>{config.name}</FormLabel>
                                <Input
                                    value={itemValue[config.name]}
                                    onChange={(event) => {
                                        updateListItemValue(config.name, event.target.value);
                                    }}
                                    multiline
                                    minRows={1}
                                    inputProps={{ 'data-1p-ignore': true }}
                                />
                            </Stack>
                        );
                    }
                    if (config.type === 'color') {
                        return (
                            <Stack
                                direction={'column'}
                                key={'tag_' + tagUID + '_list_item_' + itemIndex + '_color_' + valueIndex}
                            >
                                <FormLabel>{config.name}</FormLabel>
                                <ColorPicker
                                    controlProps={{
                                        ...controlProps,
                                        value: itemValue[config.name],
                                        name: config.name,
                                        onColorChange: (color) => {
                                            updateListItemValue(config.name, color);
                                        },
                                        simpleMode: true,
                                    }}
                                />
                            </Stack>
                        );
                    }
                    if (config.type === 'select' || config.type === 'multi-select') {
                        const isMulti = config.type === 'multi-select';
                        const value =
                            isMulti && itemValue[config.name]
                                ? itemValue[config.name].split(',')
                                : itemValue[config.name];

                        return (
                            <Stack
                                direction={'column'}
                                key={'tag_' + tagUID + '_list_item_' + itemIndex + '_select_' + valueIndex}
                            >
                                <FormLabel>{config.name}</FormLabel>
                                <AsyncReactSelect
                                    value={value}
                                    sortValues={false}
                                    update={(value: any) => {
                                        updateListItemValue(
                                            config.name,
                                            Array.isArray(value) ? value.join(',') : value,
                                        );
                                    }}
                                    loadOptions={(inputValue, init) =>
                                        loadOptions(
                                            inputValue,
                                            JSON.stringify(config.options) ?? '',
                                            config.source ?? 'static',
                                            itemValue[config.name],
                                            isMulti ? 'Y' : 'N',
                                            init,
                                            itemIndex,
                                        )
                                    }
                                    isMulti={isMulti}
                                    preloadOptions
                                />
                            </Stack>
                        );
                    }
                    if (config.type === 'file') {
                        return (
                            <Stack
                                direction={'column'}
                                sx={{ overflow: 'hidden' }}
                                key={'tag_' + tagUID + '_list_item_' + itemIndex + '_file_' + valueIndex}
                            >
                                <FormLabel>{config.name}</FormLabel>
                                <Grid container direction={'row'} spacing={2} alignItems={'center'}>
                                    <Grid item xs={3}>
                                        <UploadFile
                                            key={tagUID + '_list_item_' + itemIndex + '_file_' + valueIndex}
                                            controlProps={{
                                                ...controlProps,
                                                accept: 'image/*',
                                                onChange: (event) => {
                                                    uploadButtonClick(
                                                        event,
                                                        tagUID,
                                                        itemIndex,
                                                        valueIndex,
                                                        config.name,
                                                    );
                                                },
                                                showRemoveAction: false,
                                                maxFileSizeMB: 512,
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={9}>
                                        <Typography>{itemValue[config.name]}</Typography>
                                    </Grid>
                                </Grid>
                            </Stack>
                        );
                    }
                }
            })}
        </Stack>
    ) : null;
}

export default ListItem;
